import FileDownload from "js-file-download";
import { notificationCreator } from "model/system/utils";
import { put, call, takeLatest } from "redux-saga/effects";
import { getProductList } from "model/dashboardProducts/dProductsAPI";
import { typeProductsFilters } from "model/dashboardProducts/dProductsReducer";
import {
  logoutSagaAC,
  addNotificationAC,
  dashboardLoaderAC,
} from "../system/systemActionCreators";
import {
  dOrdersSetDataAC,
  dOrdersAddItemSagaAC,
  dOrdersSetDataSagaAC,
  dOrdersEditItemSagaAC,
  dOrdersSetPaginationAC,
  dOrdersExportDataSagaAC,
  dOrdersSetSaleDetailItemC,
  dOrdersSetDetailItemFullAC,
  dOrdersSetFilteredProductsAC,
  dOrdersChangeStatusBulkSagaAC,
  dOrdersSetDetailItemFullSagaAC,
  dOrdersProductsSetPaginationAC,
  dOrdersSetSaleDetailItemSagaAC,
  dOrdersSelectedExportDataSagaAC,
  dOrdersSetFilteredProductsSagaAC,
} from "model/dashboardOrders/dOrdersActionCreators";
import {
  typeOrdersFilters,
  typeOrdersItemSlim,
} from "model/dashboardOrders/dOrdersReducer";
import {
  getSaleById,
  addOrdersItem,
  getOrdersList,
  patchBulkOrders,
  changeOrdersItem,
  getFullOrderById,
  typeNewOrderRequest,
  typeEditedOrderRequest,
  exportOrdersListWithFilters,
} from "model/dashboardOrders/dOrdersAPI";
import {
  typePagination,
  paginationMapper,
  typeRequestPagination,
} from "model/system/constants";

// Temp data list mapper
const tempDataMapper = (tempResponseData: any): typeOrdersItemSlim[] => {
  return tempResponseData.map((item: typeOrdersItemSlim) => {
    return {
      checked: false,
      address: item.address,
      collectorId: item.courierId,
      comment: item.comment,
      courier: item.courier,
      courierId: item.courierId,
      createdBy: item.createdBy,
      customerName: item.customerName,
      dateCreated: item.dateCreated,
      dateUpdated: item.dateUpdated,
      discount: item.discount,
      id: item.id,
      number: item.number,
      payments: item.payments,
      phone: item.phone,
      servicePayment: item.servicePayment,
      status: item.status,
      store: item.store,
      updatedBy: item.updatedBy,
      userCreated: item.userCreated,
      userUpdated: item.userUpdated,
      externalId: item.externalId,
      sale: item.sale,
    };
  });
};

// Get data function-generator
function* getData(payload: {
  filters: typeOrdersFilters;
  paginationRequest: typeRequestPagination;
}) {
  let response = yield call(getOrdersList, payload);

  if (response.status === 200) {
    const tempData: typeOrdersItemSlim[] = tempDataMapper(
      response.data.content
    );
    const pagination: typePagination = paginationMapper(response.data);

    yield put(dOrdersSetDataAC(tempData));
    yield put(dOrdersSetPaginationAC(pagination));
  } else {
    yield errorGenerator(response);
  }
}

// Error notification generator
function* errorGenerator(response: any) {
  if (response.status === 401) {
    yield put(logoutSagaAC());
  } else if (
    response.status === 400 &&
    response.data.description === "Order can not be changed"
  ) {
    const id = new Date();
    yield put(
      addNotificationAC({
        id: id.getTime().toString(),
        message: "ERROR_ORDER_CANNOT_BE_CHANGED",
      })
    );
  } else {
    const id = new Date();
    yield put(
      addNotificationAC({
        id: id.getTime().toString(),
        message: "ERROR_SERVER_CONNECTION",
      })
    );
  }
  console.log(response);
}

// ---------------------------------------------------------------------------------------------------------------------
// Saga set data filtredProduct
export function* dOrdersSetDataWorker(actionObject: {
  type: string;
  payload: {
    filters: typeOrdersFilters;
    paginationRequest: typeRequestPagination;
  };
}) {
  yield put(dashboardLoaderAC(true));

  yield getData(actionObject.payload);

  yield put(dashboardLoaderAC(false));
}

export function* dOrdersSetDataWatcher() {
  yield takeLatest(dOrdersSetDataSagaAC.type, dOrdersSetDataWorker);
}

// Saga set data filtered product
export function* dOrdersSetFilteredProductWorker(actionObject: {
  type: string;
  payload: {
    filters: typeProductsFilters;
    paginationRequest: typeRequestPagination;
  };
}) {
  yield put(dashboardLoaderAC(true));

  let response = yield call(getProductList, actionObject.payload);

  if (response.status === 200) {
    yield put(dOrdersSetFilteredProductsAC(response.data?.content || []));

    const pagination: typePagination = paginationMapper(response.data);

    yield put(dOrdersProductsSetPaginationAC(pagination));
  } else {
    yield errorGenerator(response);
  }
  yield put(dashboardLoaderAC(false));
}

export function* dOrdersSetFilteredProductWatcher() {
  yield takeLatest(
    dOrdersSetFilteredProductsSagaAC.type,
    dOrdersSetFilteredProductWorker
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Saga add data item
export function* dOrdersAddItemWorker(actionObject: {
  type: string;
  payload: {
    itemObject: typeNewOrderRequest;
    filters: typeOrdersFilters;
    paginationRequest: typeRequestPagination;
  };
}) {
  yield put(dashboardLoaderAC(true));

  let response = yield call(addOrdersItem, {
    dataObject: actionObject.payload.itemObject,
  });

  if (response.status === 200) {
    yield notificationCreator("SUCCESS_ADD_ITEM");
    yield getData({
      filters: actionObject.payload.filters,
      paginationRequest: actionObject.payload.paginationRequest,
    });
  } else {
    yield errorGenerator(response);
  }
  yield put(dashboardLoaderAC(false));
}

export function* dOrdersAddDataWatcher() {
  yield takeLatest(dOrdersAddItemSagaAC.type, dOrdersAddItemWorker);
}

// ---------------------------------------------------------------------------------------------------------------------
// // Saga edit data item
export function* dOrdersEditDataItemWorker(actionObject: {
  type: string;
  payload: {
    detailItemObject: typeEditedOrderRequest;
    filters: typeOrdersFilters;
    paginationRequest: typeRequestPagination;
  };
}) {
  yield put(dashboardLoaderAC(true));

  let response = yield call(changeOrdersItem, {
    dataObject: actionObject.payload.detailItemObject,
  });

  if (response.status === 200) {
    yield notificationCreator("SUCCESS_CHANGE_ITEM");

    yield getData({
      filters: actionObject.payload.filters,
      paginationRequest: actionObject.payload.paginationRequest,
    });
  } else {
    yield errorGenerator(response);
  }

  yield put(dashboardLoaderAC(false));
}

export function* dOrdersEditDataWatcher() {
  yield takeLatest(dOrdersEditItemSagaAC.type, dOrdersEditDataItemWorker);
}

// // ---------------------------------------------------------------------------------------------------------------------

// Saga set sale details
export function* dOrdersSetGetSaleDetailsWorker(actionObject: {
  type: string;
  payload: { saleId: string };
}) {
  yield put(dashboardLoaderAC(true));

  //console.log('SAGA', actionObject.payload.saleId)
  let response = yield call(getSaleById, actionObject.payload);

  if (response.status === 200) {
    yield put(dOrdersSetSaleDetailItemC(response.data));
  } else {
    yield errorGenerator(response);
  }
  yield put(dashboardLoaderAC(false));
}

export function* dOrdersSetSaleDetailsItemWatcher() {
  yield takeLatest(
    dOrdersSetSaleDetailItemSagaAC.type,
    dOrdersSetGetSaleDetailsWorker
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// // Saga edit data item
export function* dOrdersChangeStatusBulkWorker(actionObject: {
  type: string;
  payload: {
    data: { id: string; status: string }[];
    filters: typeOrdersFilters;
    paginationRequest: typeRequestPagination;
  };
}) {
  yield put(dashboardLoaderAC(true));

  let response = yield call(patchBulkOrders, {
    data: actionObject.payload.data,
  });

  if (response.status === 200) {
    yield notificationCreator("SUCCESS_CHANGE_ITEM");

    yield getData({
      filters: actionObject.payload.filters,
      paginationRequest: actionObject.payload.paginationRequest,
    });
  } else {
    yield errorGenerator(response);
  }

  yield put(dashboardLoaderAC(false));
}

export function* dOrdersBulkChangeStatusWatcher() {
  yield takeLatest(
    dOrdersChangeStatusBulkSagaAC.type,
    dOrdersChangeStatusBulkWorker
  );
}

// get full orders by id and set ti detailDataFull

export function* dOrdersSetFullDetailsItemWorker(actionObject: {
  type: string;
  payload: {
    orderId: string;
  };
}) {
  yield put(dashboardLoaderAC(true));
  let response = yield call(getFullOrderById, {
    orderId: actionObject.payload.orderId,
  });

  if (response.status === 200) {
    yield put(dOrdersSetDetailItemFullAC(response.data));
  } else {
    yield errorGenerator(response);
  }
  yield put(dashboardLoaderAC(false));
}

export function* dOrdersSetFullDetailsItemWatcher() {
  yield takeLatest(
    dOrdersSetDetailItemFullSagaAC.type,
    dOrdersSetFullDetailsItemWorker
  );
}

export function* dOrdersExportDataWorker(actionObject: {
  type: string;
  payload: { filters: typeOrdersFilters; lang: string };
}) {
  yield put(dashboardLoaderAC(true));

  let response = yield call(exportOrdersListWithFilters, {
    lang: actionObject.payload.lang,
    filters: actionObject.payload.filters,
  });

  if (response.status === 200) {
    if (response?.data?.exportType === "SYNC" && response?.data?.result) {
      const binaryString = atob(response?.data?.result);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      FileDownload(blob, "orders.xlsx");
    } else {
      yield notificationCreator("SUCCESS_EXPORT");
    }
  } else {
    yield errorGenerator(response);
  }

  yield put(dashboardLoaderAC(false));
}

export function* dOrdersExportDataWatcher() {
  yield takeLatest(dOrdersExportDataSagaAC.type, dOrdersExportDataWorker);
}

// Export selected sales data saga
export function* dOrdersSelectedExportDataWorker(actionObject: {
  type: string;
  payload: { salesIds: string[]; lang: string };
}) {
  yield put(dashboardLoaderAC(true));

  let response = yield call(exportOrdersListWithFilters, {
    lang: actionObject.payload.lang,
    selectedOrderIds: actionObject.payload.salesIds,
  });

  if (response.status === 200) {
    if (response?.data?.exportType === "SYNC" && response?.data?.result) {
      const binaryString = atob(response?.data?.result);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      FileDownload(blob, "selected-orders.xlsx");
    } else {
      yield notificationCreator("SUCCESS_EXPORT");
    }
  } else {
    yield errorGenerator(response);
  }

  yield put(dashboardLoaderAC(false));
}

export function* dOrdersSelectedExportDataWatcher() {
  yield takeLatest(
    dOrdersSelectedExportDataSagaAC.type,
    dOrdersSelectedExportDataWorker
  );
}
